/* eslint-disable */
import React, { useEffect, useMemo, useState } from "react";
import {
  addDoc,
  collection,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  Timestamp,
  where,
} from "firebase/firestore";
import { db } from "../../../firebase";

const BEHAVIOUR_LOCK_HOUR = 18;

const POSITIVE_CATEGORIES = [
  { key: "helping_others", label: "Helping others", points: "+2" },
  { key: "good_work", label: "Good work", points: "+2" },
  { key: "participation", label: "Participation", points: "+1" },
  { key: "kindness", label: "Kindness", points: "+2" },
  { key: "leadership", label: "Leadership", points: "+3" },
  { key: "excellent_homework", label: "Excellent homework", points: "+2" },
  { key: "improved_attendance", label: "Improved attendance", points: "+2" },
  { key: "good_manners", label: "Good manners", points: "+1" },
];

const NEGATIVE_CATEGORIES = [
  { key: "disruption", label: "Disruption", points: "-1" },
  { key: "not_listening", label: "Not listening", points: "-1" },
  { key: "fighting", label: "Fighting", points: "-3" },
  { key: "bullying", label: "Bullying", points: "-4" },
  { key: "lateness_to_lesson", label: "Lateness to lesson", points: "-1" },
  { key: "homework_missing", label: "Homework missing", points: "-1" },
  { key: "uniform_issue", label: "Uniform issue", points: "-1" },
  { key: "safety_concern", label: "Safety concern", points: "-3" },
  { key: "rudeness", label: "Rudeness", points: "-2" },
];

function displayStudentName(student) {
  return student?.name || student?.fullName || student?.displayName || "Unnamed Student";
}

function classDisplayName(item) {
  return item?.name || item?.className || item?.yearGroup || item?.label || item?.id || "Class";
}

function formatPoints(points) {
  return String(points || "").trim() || "0";
}

function formatTimestamp(ts) {
  try {
    const date = ts?.toDate ? ts.toDate() : null;
    if (!date) return "Just now";
    return date.toLocaleString([], {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "Just now";
  }
}

function categoryTone(type) {
  return type === "negative"
    ? { softBg: "#fff1f2", softBorder: "1px solid #fecdd3", softColor: "#be123c", accent: "#ef4444" }
    : { softBg: "#ecfdf3", softBorder: "1px solid #bbf7d0", softColor: "#166534", accent: "#22c55e" };
}

function isAfterBehaviourCutoff() {
  const now = new Date();
  return now.getHours() >= BEHAVIOUR_LOCK_HOUR;
}

export default function TeacherBehaviourEngine({
  schoolId,
  teacher,
  selectedClass,
  students = [],
  locked = false,
  brandPrimary = "#4f46e5",
}) {
  const [queryText, setQueryText] = useState("");
  const [selectedStudentId, setSelectedStudentId] = useState("");
  const [type, setType] = useState("positive");
  const [selectedCategoryKey, setSelectedCategoryKey] = useState("");
  const [points, setPoints] = useState("");
  const [note, setNote] = useState("");
  const [notifyParent, setNotifyParent] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [recentLogs, setRecentLogs] = useState([]);
  const [loadingFeed, setLoadingFeed] = useState(true);
  const [timeLocked, setTimeLocked] = useState(isAfterBehaviourCutoff());

  useEffect(() => {
    const timer = window.setInterval(() => {
      setTimeLocked(isAfterBehaviourCutoff());
    }, 30000);

    return () => window.clearInterval(timer);
  }, []);

  const behaviourLocked = locked || timeLocked;

  const categories = useMemo(
    () => (type === "positive" ? POSITIVE_CATEGORIES : NEGATIVE_CATEGORIES),
    [type]
  );

  const filteredStudents = useMemo(() => {
    const q = String(queryText || "").trim().toLowerCase();
    if (!q) return students;

    return students.filter((student) =>
      [displayStudentName(student), student?.admissionNumber, student?.admissionNo]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [students, queryText]);

  const selectedStudent = useMemo(
    () => students.find((student) => student.id === selectedStudentId) || null,
    [students, selectedStudentId]
  );

  const selectedCategory = useMemo(
    () => categories.find((item) => item.key === selectedCategoryKey) || null,
    [categories, selectedCategoryKey]
  );

  const summary = useMemo(() => {
    const positive = recentLogs.filter((log) => log.type === "positive").length;
    const negative = recentLogs.filter((log) => log.type === "negative").length;
    return { total: recentLogs.length, positive, negative };
  }, [recentLogs]);

  useEffect(() => {
    setSelectedCategoryKey("");
    setPoints("");
  }, [type]);

  useEffect(() => {
    if (selectedCategory) setPoints(selectedCategory.points);
  }, [selectedCategory]);

  useEffect(() => {
    if (!schoolId) {
      setRecentLogs([]);
      setLoadingFeed(false);
      return undefined;
    }

    setLoadingFeed(true);

    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    sevenDaysAgo.setHours(0, 0, 0, 0);

    const logsQuery = query(
      collection(db, "schools", schoolId, "behaviourLogs"),
      where("createdAt", ">=", Timestamp.fromDate(sevenDaysAgo)),
      orderBy("createdAt", "desc"),
      limit(80)
    );

    const unsub = onSnapshot(
      logsQuery,
      (snap) => {
        setRecentLogs(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoadingFeed(false);
      },
      (err) => {
        console.error("Behaviour feed load failed:", err);
        setRecentLogs([]);
        setLoadingFeed(false);
      }
    );

    return () => unsub();
  }, [schoolId]);

  async function handleQuickLog() {
    if (behaviourLocked) {
      setSuccess("");
      setError("Behaviour logging is locked after 6:00pm. Contact admin for late entry.");
      return;
    }

    if (!schoolId) return setError("Missing school context.");
    if (!selectedClass?.id) return setError("Select a class first.");
    if (!selectedStudent) return setError("Select a student.");
    if (!selectedCategory) return setError("Select a behaviour category.");

    try {
      setSaving(true);
      setError("");
      setSuccess("");

      await addDoc(collection(db, "schools", schoolId, "behaviourLogs"), {
        schoolId,
        studentId: selectedStudent.id,
        studentName: displayStudentName(selectedStudent),
        classId: selectedClass.id,
        className: classDisplayName(selectedClass),
        teacherUid: teacher?.uid || "",
        teacherName: teacher?.name || teacher?.displayName || "Teacher",
        type,
        behaviourType: type,
        category: selectedCategory.key,
        categoryLabel: selectedCategory.label,
        points: points || selectedCategory.points,
        note: String(note || "").trim(),
        notifyParent: !!notifyParent,
        parentCategory: "behaviour",
        updateCategory: "behaviour",
        notificationCategory: "behaviour",
        module: "behaviour",
        source: "behaviour_log",
        sourceType: "teacher_behaviour",
        notificationStatus: notifyParent ? "pending_server_notification" : "not_requested",
        status: "logged",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      setSuccess("Behaviour logged successfully.");
      setSelectedStudentId("");
      setQueryText("");
      setSelectedCategoryKey("");
      setPoints("");
      setNote("");
      setNotifyParent(true);
    } catch (err) {
      console.error("Behaviour log failed:", err);
      setError("Failed to log behaviour.");
    } finally {
      setSaving(false);
    }
  }

  const lockedStyle = behaviourLocked ? styles.lockedControl : {};

  return (
    <div style={styles.wrap} className="pp-teacher-behaviour">
      <style>{responsiveCss}</style>
      <div style={styles.topGrid} className="pp-behaviour-top-grid">
        <div style={styles.heroCard}>
          <div style={styles.eyebrow}>Teacher behaviour engine</div>
          <h3 style={styles.title}>Quick-log behaviour</h3>
          <p style={styles.text}>
            Log praise or concern in under 60 seconds with student-first quick actions.
          </p>
        </div>

        <div style={{ ...styles.contextCard, background: brandPrimary }}>
          <div style={styles.contextLabel}>Selected class</div>
          <div style={styles.contextValue}>
            {selectedClass ? classDisplayName(selectedClass) : "No class selected"}
          </div>
          <div style={styles.contextSub}>
            {students.length} student{students.length === 1 ? "" : "s"} available
          </div>
        </div>
      </div>

      {error ? <div style={styles.error}>{error}</div> : null}
      {success ? <div style={styles.success}>{success}</div> : null}

      <div style={styles.mainGrid} className="pp-behaviour-main-grid">
        <div style={styles.leftCol} className="pp-behaviour-left">
          <div style={styles.card}>
            <div style={styles.sectionHeader}>
              <div style={styles.sectionEyebrow}>Step 1</div>
              <div style={styles.sectionTitle}>Choose student</div>
            </div>

            <input
              value={queryText}
              onChange={(e) => setQueryText(e.target.value)}
              placeholder="Search student"
              style={styles.searchInput}
              disabled={behaviourLocked}
            />

            <div style={styles.studentCountLine}>
              <span>{filteredStudents.length} shown</span>
              <span>{students.length} in class</span>
            </div>

            <div style={styles.studentList} className="pp-behaviour-students">
              {filteredStudents.length ? (
                filteredStudents.map((student) => {
                  const active = selectedStudentId === student.id;
                  return (
                    <button
                      key={student.id}
                      type="button"
                      disabled={behaviourLocked}
                      onClick={() => setSelectedStudentId(student.id)}
                      style={{
                        ...styles.studentChip,
                        ...(active
  ? {
      borderColor: brandPrimary,
      background: `${brandPrimary}18`,
      color: brandPrimary,
    }
  : {}),
                        ...lockedStyle,
                      }}
                    >
                      <span style={styles.studentChipName}>{displayStudentName(student)}</span>
                    </button>
                  );
                })
              ) : (
                <div style={styles.emptyState}>No students found for this search.</div>
              )}
            </div>
          </div>

          <div style={styles.card}>
            <div style={styles.sectionHeader}>
              <div style={styles.sectionEyebrow}>Step 2</div>
              <div style={styles.sectionTitle}>Behaviour type</div>
            </div>

            <div style={styles.toggleRow}>
              <button
                type="button"
                disabled={behaviourLocked}
                onClick={() => setType("positive")}
                style={{
                  ...styles.typeBtn,
                  ...(type === "positive" ? styles.typeBtnPositiveActive : {}),
                  ...lockedStyle,
                }}
              >
                Positive
              </button>

              <button
                type="button"
                disabled={behaviourLocked}
                onClick={() => setType("negative")}
                style={{
                  ...styles.typeBtn,
                  ...(type === "negative" ? styles.typeBtnNegativeActive : {}),
                  ...lockedStyle,
                }}
              >
                Negative
              </button>
            </div>

            <div style={styles.categoryGrid} className="pp-behaviour-categories">
              {categories.map((item) => {
                const active = selectedCategoryKey === item.key;
                return (
                  <button
                    key={item.key}
                    type="button"
                    disabled={behaviourLocked}
                    onClick={() => setSelectedCategoryKey(item.key)}
                    style={{
                      ...styles.categoryChip,
                      ...(active
  ? {
      borderColor: brandPrimary,
      background: `${brandPrimary}18`,
    }
  : {}),
                      ...lockedStyle,
                    }}
                  >
                    <span style={styles.categoryLabel}>{item.label}</span>
                    <span style={styles.categoryPoints}>{item.points}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div style={styles.rightCol} className="pp-behaviour-right">
          <div style={styles.card}>
            <div style={styles.sectionHeader}>
              <div style={styles.sectionEyebrow}>Step 3</div>
              <div style={styles.sectionTitle}>Finish log</div>
            </div>

            <div style={styles.summaryBox}>
              <SummaryRow label="Student" value={selectedStudent ? displayStudentName(selectedStudent) : "Not selected"} />
              <SummaryRow label="Type" value={type === "positive" ? "Positive" : "Negative"} />
              <SummaryRow label="Category" value={selectedCategory?.label || "Not selected"} />
            </div>

            <div style={styles.fieldBlock}>
              <label style={styles.label}>Points</label>
              <input
                value={points}
                onChange={(e) => setPoints(e.target.value)}
                placeholder="+2 or -1"
                style={styles.input}
                disabled={behaviourLocked}
              />
            </div>

            <div style={styles.fieldBlock}>
              <label style={styles.label}>Note</label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Add context for staff or parent visibility"
                style={styles.textarea}
                disabled={behaviourLocked}
              />
            </div>

            <label style={{ ...styles.checkboxRow, ...lockedStyle }}>
              <input
                type="checkbox"
                checked={notifyParent}
                disabled={behaviourLocked}
                onChange={(e) => setNotifyParent(e.target.checked)}
              />
              <span>Notify parent</span>
            </label>

            <button
              type="button"
              onClick={handleQuickLog}
              disabled={saving || behaviourLocked}
style={{
  ...styles.submitBtn,
  background: brandPrimary,
  ...(saving || behaviourLocked ? styles.submitBtnDisabled : {}),
}}
            >
              {behaviourLocked ? "Locked after 6:00pm" : saving ? "Logging..." : "Log Behaviour"}
            </button>
          </div>

          <div style={styles.card}>
            <div style={styles.sectionHeader}>
              <div style={styles.sectionEyebrow}>Live feed</div>
              <div style={styles.sectionTitle}>Recent behaviour</div>
            </div>

            <div style={styles.feedStats}>
              <FeedStat label="Recent logs" value={summary.total} />
              <FeedStat label="Positive" value={summary.positive} color="#16a34a" />
              <FeedStat label="Negative" value={summary.negative} color="#dc2626" />
            </div>

            {loadingFeed ? (
              <div style={styles.emptyState}>Loading behaviour feed...</div>
            ) : recentLogs.length ? (
              <div style={styles.feedList} className="pp-behaviour-feed">
                {recentLogs.map((log) => {
                  const tone = categoryTone(log.type);
                  return (
                    <div key={log.id} style={{ ...styles.feedItem, borderLeft: `4px solid ${tone.accent}` }}>
                      <div style={styles.feedTop}>
                        <div>
                          <div style={styles.feedName}>{log.studentName || "Student"}</div>
                          <div style={styles.feedMeta}>{log.className || "Class"} • {log.teacherName || "Teacher"}</div>
                        </div>

                        <span style={{ ...styles.feedTypePill, background: tone.softBg, color: tone.softColor, border: tone.softBorder }}>
                          {log.type === "negative" ? "Negative" : "Positive"}
                        </span>
                      </div>

                      <div style={styles.feedMiddle}>
                        <span style={styles.feedCategory}>{log.categoryLabel || log.category || "Category"}</span>
                        <span style={styles.feedPoints}>{formatPoints(log.points)}</span>
                      </div>

                      {log.note ? <div style={styles.feedNote}>{log.note}</div> : null}

                      <div style={styles.feedBottom}>
                        <span>{log.notifyParent ? "Parent notification pending" : "No parent alert"}</span>
                        <span>{formatTimestamp(log.createdAt)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={styles.emptyState}>No recent behaviour logs yet.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function SummaryRow({ label, value }) {
  return (
    <div style={styles.summaryRow}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function FeedStat({ label, value, color = "#112042" }) {
  return (
    <div style={styles.feedStat}>
      <span>{label}</span>
      <strong style={{ color }}>{value}</strong>
    </div>
  );
}


const responsiveCss = `
  .pp-teacher-behaviour,
  .pp-teacher-behaviour * {
    box-sizing: border-box;
  }

  @media (max-width: 1320px) {
    .pp-behaviour-main-grid {
      grid-template-columns: minmax(0, 1.45fr) minmax(330px, 0.9fr) !important;
    }

    .pp-behaviour-students,
    .pp-behaviour-categories {
      grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
    }
  }

  @media (max-width: 1040px) {
    .pp-behaviour-top-grid,
    .pp-behaviour-main-grid {
      grid-template-columns: minmax(0, 1fr) !important;
    }

    .pp-behaviour-right {
      grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
      align-items: start;
    }

    .pp-behaviour-feed {
      max-height: 520px !important;
    }
  }

  @media (max-width: 760px) {
    .pp-behaviour-right {
      grid-template-columns: minmax(0, 1fr) !important;
    }

    .pp-behaviour-students,
    .pp-behaviour-categories {
      grid-template-columns: minmax(0, 1fr) !important;
    }
  }
`;

const styles = {
  wrap: {
    display: "grid",
    gap: 16,
    minWidth: 0,
    width: "100%",
  },

  topGrid: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) minmax(260px, 320px)",
    gap: 16,
    alignItems: "stretch",
    minWidth: 0,
  },

  heroCard: {
    borderRadius: 24,
    background: "#ffffff",
    border: "1px solid #dbe7ff",
    padding: 20,
    boxShadow: "0 12px 34px rgba(15,23,42,0.05)",
  },

  contextCard: {
    borderRadius: 24,
    padding: 20,
    color: "#ffffff",
    background: "linear-gradient(135deg, #4f46e5 0%, #6d5cff 100%)",
    boxShadow: "0 18px 40px rgba(79,70,229,0.18)",
    display: "grid",
    alignContent: "center",
  },

  contextLabel: {
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.14em",
    fontWeight: 900,
    color: "rgba(255,255,255,0.78)",
    marginBottom: 8,
  },

  contextValue: {
    fontSize: 28,
    lineHeight: 1,
    fontWeight: 950,
  },

  contextSub: {
    marginTop: 8,
    color: "rgba(255,255,255,0.88)",
    fontSize: 13,
    fontWeight: 750,
  },

  eyebrow: {
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.13em",
    color: "#64748b",
    fontWeight: 950,
    marginBottom: 8,
  },

  title: {
    margin: 0,
    fontSize: 26,
    lineHeight: 1.05,
    color: "#112042",
    fontWeight: 950,
    letterSpacing: "-0.04em",
  },

  text: {
    margin: "10px 0 0",
    color: "#64748b",
    lineHeight: 1.6,
    fontSize: 14,
    fontWeight: 650,
  },

  success: {
    borderRadius: 18,
    padding: 14,
    background: "#ecfdf3",
    border: "1px solid #bbf7d0",
    color: "#166534",
    fontWeight: 900,
  },

  error: {
    borderRadius: 18,
    padding: 14,
    background: "#fff1f2",
    border: "1px solid #fecdd3",
    color: "#be123c",
    fontWeight: 900,
  },

  mainGrid: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1.65fr) minmax(360px, 0.9fr)",
    gap: 18,
    alignItems: "start",
    minWidth: 0,
  },

  leftCol: {
    display: "grid",
    gap: 16,
    minWidth: 0,
  },

  rightCol: {
    display: "grid",
    gap: 16,
    minWidth: 0,
    alignSelf: "start",
  },

  card: {
    borderRadius: 24,
    background: "#ffffff",
    border: "1px solid #dbe7ff",
    padding: 18,
    boxShadow: "0 12px 34px rgba(15,23,42,0.045)",
  },

  sectionHeader: {
    marginBottom: 14,
  },

  sectionEyebrow: {
    fontSize: 10,
    textTransform: "uppercase",
    letterSpacing: "0.14em",
    color: "#64748b",
    fontWeight: 950,
    marginBottom: 5,
  },

  sectionTitle: {
    color: "#112042",
    fontSize: 20,
    lineHeight: 1.05,
    fontWeight: 950,
  },

  searchInput: {
    width: "100%",
    borderRadius: 16,
    border: "1px solid #dbe7ff",
    padding: "13px 14px",
    outline: "none",
    boxSizing: "border-box",
    color: "#112042",
  },

  studentCountLine: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    marginTop: 9,
    color: "#64748b",
    fontSize: 12,
    fontWeight: 800,
  },

  studentList: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 10,
    marginTop: 10,
    maxHeight: 300,
    overflowY: "auto",
    paddingRight: 6,
    WebkitOverflowScrolling: "touch",
    overscrollBehavior: "contain",
  },

  studentChip: {
    width: "100%",
    minWidth: 0,
    minHeight: 48,
    borderRadius: 14,
    border: "1px solid #dbe7ff",
    background: "#fbfdff",
    padding: "11px 13px",
    color: "#112042",
    cursor: "pointer",
    fontWeight: 850,
    textAlign: "left",
    overflow: "hidden",
  },

  studentChipActive: {
    borderColor: "#635bff",
    background: "#eef2ff",
    color: "#3730a3",
  },

  studentChipName: {
    fontWeight: 950,
  },

  toggleRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 10,
    marginBottom: 12,
  },

  typeBtn: {
    borderRadius: 16,
    border: "1px solid #dbe7ff",
    background: "#ffffff",
    padding: "13px 14px",
    color: "#112042",
    fontWeight: 950,
    cursor: "pointer",
  },

  typeBtnPositiveActive: {
    background: "#ecfdf3",
    color: "#166534",
    borderColor: "#bbf7d0",
  },

  typeBtnNegativeActive: {
    background: "#fff1f2",
    color: "#be123c",
    borderColor: "#fecdd3",
  },

  categoryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 10,
    maxHeight: 310,
    overflowY: "auto",
    paddingRight: 6,
    WebkitOverflowScrolling: "touch",
    overscrollBehavior: "contain",
  },

  categoryChip: {
    borderRadius: 14,
    border: "1px solid #dbe7ff",
    background: "#fbfdff",
    padding: "12px 13px",
    display: "flex",
    justifyContent: "space-between",
    gap: 10,
    alignItems: "center",
    color: "#112042",
    cursor: "pointer",
  },

  categoryChipActive: {
    borderColor: "#635bff",
    background: "#eef2ff",
  },

  categoryLabel: {
    fontWeight: 900,
  },

  categoryPoints: {
    fontWeight: 950,
  },

  summaryBox: {
    borderRadius: 18,
    border: "1px solid #dbe7ff",
    background: "#f8fbff",
    overflow: "hidden",
    marginBottom: 14,
  },

  summaryRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    padding: "12px 14px",
    borderBottom: "1px solid #edf2ff",
    color: "#64748b",
    fontWeight: 800,
  },

  fieldBlock: {
    display: "grid",
    gap: 7,
    marginBottom: 12,
  },

  label: {
    color: "#112042",
    fontSize: 12,
    fontWeight: 950,
  },

  input: {
    width: "100%",
    borderRadius: 16,
    border: "1px solid #dbe7ff",
    padding: "13px 14px",
    outline: "none",
    boxSizing: "border-box",
    color: "#112042",
  },

  textarea: {
    width: "100%",
    minHeight: 100,
    borderRadius: 16,
    border: "1px solid #dbe7ff",
    padding: "13px 14px",
    outline: "none",
    boxSizing: "border-box",
    resize: "vertical",
    color: "#112042",
  },

  checkboxRow: {
    display: "flex",
    gap: 10,
    alignItems: "center",
    color: "#112042",
    fontWeight: 850,
    marginBottom: 14,
  },

  submitBtn: {
    width: "100%",
    border: "none",
    borderRadius: 16,
    background: "linear-gradient(135deg, #4f46e5 0%, #7c5cff 100%)",
    color: "#ffffff",
    padding: "14px 16px",
    fontWeight: 950,
    cursor: "pointer",
    boxShadow: "0 16px 34px rgba(79,70,229,0.18)",
  },

  submitBtnDisabled: {
    opacity: 0.55,
    cursor: "not-allowed",
    boxShadow: "none",
  },

  lockedControl: {
    opacity: 0.55,
    cursor: "not-allowed",
  },

  feedStats: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 10,
    marginBottom: 12,
  },

  feedStat: {
    borderRadius: 16,
    border: "1px solid #dbe7ff",
    background: "#f8fbff",
    padding: 12,
    display: "grid",
    gap: 6,
  },

  feedList: {
    display: "grid",
    gap: 12,
    maxHeight: 700,
    overflowY: "auto",
    paddingRight: 6,
    WebkitOverflowScrolling: "touch",
    overscrollBehavior: "contain",
  },

  feedItem: {
    borderRadius: 18,
    border: "1px solid #dbe7ff",
    background: "#ffffff",
    padding: 14,
  },

  feedTop: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    alignItems: "flex-start",
    marginBottom: 10,
  },

  feedName: {
    fontSize: 16,
    fontWeight: 950,
    color: "#112042",
  },

  feedMeta: {
    marginTop: 4,
    fontSize: 12,
    color: "#64748b",
    fontWeight: 750,
  },

  feedTypePill: {
    borderRadius: 999,
    padding: "6px 10px",
    fontSize: 11,
    fontWeight: 950,
    whiteSpace: "nowrap",
  },

  feedMiddle: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    alignItems: "center",
    marginBottom: 8,
  },

  feedCategory: {
    color: "#112042",
    fontWeight: 900,
  },

  feedPoints: {
    color: "inherit",
    fontWeight: 950,
  },

  feedNote: {
    marginBottom: 8,
    color: "#475569",
    fontSize: 13,
    lineHeight: 1.55,
  },

  feedBottom: {
    display: "flex",
    justifyContent: "space-between",
    gap: 12,
    paddingTop: 8,
    borderTop: "1px solid #edf2ff",
    color: "#64748b",
    fontSize: 12,
    fontWeight: 800,
  },

  emptyState: {
    borderRadius: 16,
    border: "1px solid #dbe7ff",
    background: "#f8fbff",
    padding: 14,
    color: "#64748b",
    fontWeight: 800,
  },
};
